import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Building2,
  RefreshCcw,
  Link2,
  Unlink,
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  CreditCard,
  Wallet,
  ChevronRight,
  ExternalLink,
  Info,
  Copy } from
'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription } from
'@/shared/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
"@/shared/components/ui/tabs";

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const BankFeedsIntegration = () => {const { t } = useTranslation("finance");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [connections, setConnections] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchIntegrationStatus();
    fetchConnections();
  }, []);

  const fetchIntegrationStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bank-feeds/status`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      toast.error('Failed to load integration status');
    }
  };

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bank-feeds/connections`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);

        // Fetch accounts if there are connections
        if (data.connections?.length > 0) {
          await fetchAccounts();
        }
      }
    } catch (err) {
      console.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bank-feeds/accounts`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load accounts');
    }
  };

  const fetchTransactions = async (accountId) => {
    try {
      const res = await fetch(`${API_URL}/api/bank-feeds/accounts/${accountId}/transactions?limit=50`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions || []);
        setSelectedAccount(accountId);
      }
    } catch (err) {
      toast.error('Failed to load transactions');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/bank-feeds/sync`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Bank data synchronized successfully');
        await fetchAccounts();
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="bank-feeds-integration">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <Building2 className="h-7 w-7 text-white" />
            </div>{t("bankFeeds", "Bank Feeds")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("realtimeBankTransaction", "Real-time bank transaction feeds via Open Finance Brasil")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowSetupModal(true)}>
            <Settings className="h-4 w-4" />{t("setup", "Setup")}

          </Button>
          <Button
            className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600"
            onClick={handleSync}
            disabled={syncing}>
            
            <RefreshCcw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />{t("sync", "Sync")}

          </Button>
        </div>
      </div>

      {/* Integration Status */}
      {status &&
      <Card className={`card-elevated ${status.ready_for_production ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {status.ready_for_production ?
              <CheckCircle className="h-6 w-6 text-green-600" /> :

              <AlertCircle className="h-6 w-6 text-amber-600" />
              }
                <div>
                  <h3 className="font-semibold">
                    {status.ready_for_production ? 'Integration Ready' : 'Setup Required'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {status.configured_banks}{t("of", "of")}{status.total_banks}{t("banksConfiguredEnvironment", "banks configured \u2022 \n                    Environment:")}
                  <Badge variant="outline">{status.environment}</Badge>
                  </p>
                </div>
              </div>
              {!status.ready_for_production &&
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowSetupModal(true)}>{t("configure", "Configure")}


              <ChevronRight className="h-4 w-4" />
                </Button>
            }
            </div>
          </CardContent>
        </Card>
      }

      {/* Banks Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {status?.banks?.map((bank) =>
        <Card
          key={bank.bank_id}
          className={`card-elevated transition-all ${bank.api_configured ? 'hover:shadow-lg' : 'opacity-70'}`}>
          
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center border">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{bank.bank_name}</h3>
                  <p className="text-xs text-muted-foreground">{t("code", "Code:")}{bank.bank_code}</p>
                </div>
                <Badge variant={bank.api_configured ? 'default' : 'secondary'}>
                  {bank.api_configured ? 'Ready' : 'Setup'}
                </Badge>
              </div>
              {!bank.api_configured &&
            <p className="text-xs text-amber-600 mt-3 bg-amber-50 p-2 rounded">
                  {bank.setup_message}
                </p>
            }
            </CardContent>
          </Card>
        )}
      </div>

      {/* Connected Accounts */}
      {accounts.length > 0 &&
      <Tabs defaultValue="accounts" className="space-y-4">
          <TabsList>
            <TabsTrigger value="accounts">{t("accounts", "Accounts (")}{accounts.length})</TabsTrigger>
            <TabsTrigger value="transactions">{t("transactions", "Transactions")}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="accounts">
            <div className="grid gap-4 md:grid-cols-2">
              {accounts.map((account) =>
            <Card
              key={account.account_id}
              className="card-elevated hover:shadow-lg transition-all cursor-pointer"
              onClick={() => fetchTransactions(account.account_id)}>
              
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-xl">
                          <Wallet className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium">{account.bank_name}</h3>
                          <p className="text-xs text-muted-foreground capitalize">
                            {account.account_type} • {account.account_number}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {account.currency}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-muted-foreground">{t("balance", "Balance")}</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {formatCurrency(account.balance)}
                        </span>
                      </div>
                      <div className="flex justify-between items-baseline text-sm">
                        <span className="text-muted-foreground">{t("available", "Available")}</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(account.available_balance)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />{t("updated", "Updated")}
                    {new Date(account.last_updated).toLocaleTimeString('pt-BR')}
                      </span>
                      <span className="flex items-center gap-1 text-blue-600">{t("viewTransactions", "View Transactions")}

                    <ChevronRight className="h-3 w-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          </TabsContent>
          
          <TabsContent value="transactions">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />{t("recentTransactions", "Recent Transactions")}

              </CardTitle>
                {selectedAccount &&
              <CardDescription>{t("account", "Account:")}
                {selectedAccount}
                  </CardDescription>
              }
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ?
              <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">{t("selectAnAccount", "Select an account to view transactions")}

                </p>
                  </div> :

              <div className="space-y-2">
                    {transactions.map((tx, idx) =>
                <div
                  key={tx.transaction_id || idx}
                  className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors">
                  
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                    tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`
                    }>
                            {tx.type === 'credit' ?
                      <ArrowDownLeft className="h-4 w-4 text-green-600" /> :

                      <ArrowUpRight className="h-4 w-4 text-red-600" />
                      }
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(tx.date)} • {tx.category}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-semibold ${
                    tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`
                    }>
                            {tx.type === 'credit' ? '+' : ''}{formatCurrency(tx.amount)}
                          </p>
                          <p className="text-xs text-muted-foreground">{t("balance", "Balance:")}
                      {formatCurrency(tx.balance_after)}
                          </p>
                        </div>
                      </div>
                )}
                  </div>
              }
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      }

      {/* Empty State */}
      {accounts.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="p-12 text-center">
            <Building2 className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t("noBankAccounts", "No Bank Accounts Connected")}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("connectYourBank", "Connect your bank accounts to automatically import transactions and reconcile your finances")}

          </p>
            <Button
            className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600"
            onClick={() => setShowSetupModal(true)}>
            
              <Link2 className="h-4 w-4" />{t("connectBankAccount", "Connect Bank Account")}

          </Button>
          </CardContent>
        </Card>
      }

      {/* Setup Modal */}
      <Dialog open={showSetupModal} onOpenChange={setShowSetupModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />{t("bankFeedsSetup", "Bank Feeds Setup")}

            </DialogTitle>
            <DialogDescription>{t("configureYourOpen", "Configure your Open Finance Brasil integration")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Setup Instructions */}
            <div className="bg-blue-50 p-4 rounded-xl space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600" />{t("setupInstructions", "Setup Instructions")}

              </h4>
              <ol className="space-y-2 text-sm">
                {status?.setup_instructions && Object.entries(status.setup_instructions).
                filter(([key]) => key.startsWith('step')).
                map(([key, value]) =>
                <li key={key} className="flex gap-2">
                      <span className="font-mono text-blue-600">{key.replace('step_', '')}.</span>
                      <span>{value}</span>
                    </li>
                )}
              </ol>
              {status?.setup_instructions?.documentation &&
              <a
                href={status.setup_instructions.documentation}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2">{t("viewDocumentation", "View Documentation")}


                <ExternalLink className="h-3 w-3" />
                </a>
              }
            </div>

            {/* Environment Variables */}
            <div>
              <h4 className="font-semibold mb-3">{t("requiredEnvironmentVariables", "Required Environment Variables")}</h4>
              <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto">
                <div className="space-y-1">
                  <p className="text-slate-400">{t("addToBackendenv", "# Add to backend/.env file")}</p>
                  <p>{t("openfinanceenvironmentsandbox", "OPEN_FINANCE_ENVIRONMENT=sandbox")}</p>
                  <p className="text-slate-400 mt-3">{t("bancoDoBrasil", "# Banco do Brasil")}</p>
                  <p>{t("openfinancebancobrasilclientidyourclientid", "OPEN_FINANCE_BANCO_BRASIL_CLIENT_ID=your_client_id")}</p>
                  <p>{t("openfinancebancobrasilclientsecretyoursecret", "OPEN_FINANCE_BANCO_BRASIL_CLIENT_SECRET=your_secret")}</p>
                  <p className="text-slate-400 mt-3">{t("itaUnibanco", "# Ita\xFA Unibanco")}</p>
                  <p>{t("openfinanceitauclientidyourclientid", "OPEN_FINANCE_ITAU_CLIENT_ID=your_client_id")}</p>
                  <p>{t("openfinanceitauclientsecretyoursecret", "OPEN_FINANCE_ITAU_CLIENT_SECRET=your_secret")}</p>
                  <p className="text-slate-400 mt-3">{t("repeatForOther", "# ... (repeat for other banks)")}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 gap-1"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `OPEN_FINANCE_ENVIRONMENT=sandbox\nOPEN_FINANCE_BANCO_BRASIL_CLIENT_ID=\nOPEN_FINANCE_BANCO_BRASIL_CLIENT_SECRET=\nOPEN_FINANCE_ITAU_CLIENT_ID=\nOPEN_FINANCE_ITAU_CLIENT_SECRET=\nOPEN_FINANCE_BRADESCO_CLIENT_ID=\nOPEN_FINANCE_BRADESCO_CLIENT_SECRET=\nOPEN_FINANCE_SANTANDER_CLIENT_ID=\nOPEN_FINANCE_SANTANDER_CLIENT_SECRET=\nOPEN_FINANCE_CAIXA_CLIENT_ID=\nOPEN_FINANCE_CAIXA_CLIENT_SECRET=`
                  );
                  toast.success('Copied to clipboard');
                }}>
                
                <Copy className="h-4 w-4" />{t("copyTemplate", "Copy Template")}

              </Button>
            </div>

            {/* Banks Status */}
            <div>
              <h4 className="font-semibold mb-3">{t("bankConfigurationStatus", "Bank Configuration Status")}</h4>
              <div className="grid gap-2">
                {status?.banks?.map((bank) =>
                <div
                  key={bank.bank_id}
                  className="flex items-center justify-between p-3 border rounded-lg">
                  
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{bank.bank_name}</span>
                    </div>
                    {bank.api_configured ?
                  <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />{t("configured", "Configured")}

                  </Badge> :

                  <Badge variant="outline" className="text-amber-600 border-amber-200">
                        <AlertCircle className="h-3 w-3 mr-1" />{t("pending", "Pending")}

                  </Badge>
                  }
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

};

export default BankFeedsIntegration;